import { useEffect, useId, useMemo, useRef, useState } from "react";
import { MdKeyboardArrowDown, MdSearch } from "react-icons/md";
import { brandInputClass } from "./fieldStyles";
import { FieldShell } from "./FieldShell";

function toOptionValue(value) {
  return value == null ? "" : String(value);
}

function normalizeText(value) {
  return toOptionValue(value).trim().toLocaleLowerCase();
}

export function SearchSelect({
  label,
  name,
  value,
  onChange,
  onBlur,
  options = [],
  placeholder = "Buscar",
  helper,
  error,
  disabled = false,
  required = false,
  className = "",
  showLabel = true,
}) {
  const rootRef = useRef(null);
  const inputId = useId();
  const listboxId = useId();
  const [isOpen, setIsOpen] = useState(false);

  const selectedValue = toOptionValue(value);
  const selectedOption = useMemo(
    () =>
      selectedValue
        ? options.find((option) => toOptionValue(option.value) === selectedValue) ?? null
        : null,
    [options, selectedValue],
  );
  const [query, setQuery] = useState(selectedOption?.label ?? "");

  useEffect(() => {
    setQuery(selectedOption?.label ?? "");
  }, [selectedOption?.label]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeText(query);

    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) => normalizeText(option.label).includes(normalizedQuery));
  }, [options, query]);

  const commitValue = (nextValue, nextLabel) => {
    onChange?.({
      target: {
        name,
        value: nextValue,
      },
    });
    onBlur?.({
      target: {
        name,
      },
    });
    setQuery(nextLabel ?? "");
    setIsOpen(false);
  };

  const resolvedPlaceholder = placeholder || "Buscar";

  return (
    <FieldShell
      label={label}
      helper={helper}
      error={error}
      className={className}
      showLabel={showLabel}
    >
      <div ref={rootRef} className="relative z-30">
        <div className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-brand-primary/50">
          <MdSearch aria-hidden="true" className="h-5 w-5" />
        </div>
        <input
          id={inputId}
          name={name}
          value={query}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={isOpen}
          aria-invalid={Boolean(error)}
          disabled={disabled}
          required={required}
          placeholder={resolvedPlaceholder}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(Boolean(event.target.value.trim()));
          }}
          onFocus={() => {
            if (!disabled) {
              setIsOpen(true);
            }
          }}
          onBlur={(event) => {
            if (!rootRef.current?.contains(event.relatedTarget)) {
              onBlur?.({
                target: {
                  name,
                },
              });
              setIsOpen(false);
            }
          }}
          className={`${brandInputClass} pl-11 pr-11`}
        />
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-brand-primary/70">
          <MdKeyboardArrowDown
            aria-hidden="true"
            className={`h-5 w-5 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>

        {isOpen && !disabled ? (
          <div
            id={listboxId}
            role="listbox"
            aria-label={label || name}
            className="absolute z-50 mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-brand-secondary/20 bg-white/95 p-1 shadow-[0_16px_36px_rgba(20,50,135,0.14)] backdrop-blur"
          >
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-500">No encontramos coincidencias.</div>
            ) : null}

            {filteredOptions.map((option) => {
              const optionValue = toOptionValue(option.value);
              const isSelected = optionValue === selectedValue;

              return (
                <button
                  key={optionValue || option.label}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  disabled={option.disabled}
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => commitValue(optionValue, option.label)}
                  className={[
                    "flex w-full items-center px-2 py-2 text-left text-sm transition-colors",
                    isSelected
                      ? "font-semibold text-brand-primary"
                      : "text-slate-700 hover:text-brand-primary",
                    option.disabled ? "cursor-not-allowed opacity-50" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </FieldShell>
  );
}
